import React, { useEffect, useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useProfessions } from '../../../../hooks/useProfessions';

export interface SubProfOption {
  id: number;
  name: string;
  professionName: string;
}

interface Props {
  value: SubProfOption | null;
  onChange: (value: SubProfOption | null) => void;
}

export const SubProfessionPicker: React.FC<Props> = ({ value, onChange }) => {
  const { professionsList, subProfessionLibrary, getProfessions, areProfessionsLoaded } =
    useProfessions();

  useEffect(() => {
    if (!areProfessionsLoaded) getProfessions();
  }, [areProfessionsLoaded, getProfessions]);

  // Flatten the { professionId: subProfs[] } library into a single searchable
  // list, grouped under each parent profession name.
  const options = useMemo<SubProfOption[]>(() => {
    const out: SubProfOption[] = [];
    (professionsList || []).forEach((prof: { id: number; name: string }) => {
      const subs = subProfessionLibrary?.[prof.id] || [];
      subs.forEach((sub: { id: number; name: string }) => {
        out.push({ id: sub.id, name: sub.name, professionName: prof.name });
      });
    });
    return out.sort(
      (a, b) =>
        a.professionName.localeCompare(b.professionName) || a.name.localeCompare(b.name),
    );
  }, [professionsList, subProfessionLibrary]);

  return (
    <Autocomplete
      value={value}
      onChange={(_, v) => onChange(v)}
      options={options}
      groupBy={(o) => o.professionName}
      getOptionLabel={(o) => o.name || ''}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      renderInput={(params) => (
        <TextField {...params} label="Sub-profession" placeholder="Search…" size="small" />
      )}
      sx={{ width: '100%' }}
    />
  );
};
